/**
 * F606AdminHub — tabbed hub for all 606 Fitness write-bridge admin panels.
 * Rendered inside the "مركز العمليات" page (/control/f606/actions).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShoppingBag, Users, CreditCard, Trophy, Package, Megaphone,
  ScrollText, Wifi, WifiOff,
} from 'lucide-react';
import F606OrdersAdmin from './F606OrdersAdmin';
import F606UsersAdmin from './F606UsersAdmin';
import F606SubscriptionsAdmin from './F606SubscriptionsAdmin';
import F606TrainersAdmin from './F606TrainersAdmin';
import F606ProductsAdmin from './F606ProductsAdmin';
import F606BroadcastAdmin from './F606BroadcastAdmin';
import F606AuditLog from './F606AuditLog';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'orders',        icon: ShoppingBag, ar: 'الطلبات',      en: 'Orders',        Comp: F606OrdersAdmin },
  { id: 'users',         icon: Users,       ar: 'المستخدمون',   en: 'Users',         Comp: F606UsersAdmin },
  { id: 'subscriptions', icon: CreditCard,  ar: 'الاشتراكات',   en: 'Subscriptions', Comp: F606SubscriptionsAdmin },
  { id: 'trainers',      icon: Trophy,      ar: 'المدرّبون',     en: 'Trainers',      Comp: F606TrainersAdmin },
  { id: 'products',      icon: Package,     ar: 'المتجر',        en: 'Products',      Comp: F606ProductsAdmin },
  { id: 'broadcast',     icon: Megaphone,   ar: 'بثّ إعلان',     en: 'Broadcast',     Comp: F606BroadcastAdmin },
  { id: 'audit',         icon: ScrollText,  ar: 'سجلّ العمليات', en: 'Audit Log',     Comp: F606AuditLog },
];

export default function F606AdminHub({ language }) {
  const isAr = language === 'ar';
  const [tab, setTab] = useState('orders');
  const [health, setHealth] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/f606/health`)
      .then((r) => setHealth(r.data))
      .catch(() => setHealth({ reachable: false }));
  }, []);

  const connected = health?.reachable === true;
  const Active = TABS.find((t) => t.id === tab)?.Comp;

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="space-y-4" data-testid="f606-admin-hub">
      {/* Tabs + health chip */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap" data-testid="f606-hub-tabs">
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition ${
                  active
                    ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200'
                    : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.07] hover:text-white'
                }`}
                data-testid={`f606-hub-tab-${t.id}`}
              >
                <t.icon className="h-3.5 w-3.5" />
                {isAr ? t.ar : t.en}
              </button>
            );
          })}
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold ${
            connected
              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
              : health === null
                ? 'bg-white/5 border-white/10 text-white/40'
                : 'bg-red-500/15 border-red-500/40 text-red-300'
          }`}
          data-testid="f606-hub-health-chip"
        >
          {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {health === null
            ? (isAr ? 'جارٍ الفحص...' : 'Checking...')
            : connected
              ? (isAr ? 'متصل' : 'Online')
              : (isAr ? 'غير متصل' : 'Offline')}
        </span>
      </div>

      {/* Active panel */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="f606-hub-panel">
        {Active && <Active language={language} />}
      </div>
    </div>
  );
}
